import React, { useState, useEffect } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Divider,
  Breadcrumbs,
  Chip,
  Stack,
} from "@mui/material";
import MuiLink from "@mui/material/Link";
import { getTreatmentById } from "../../api/treatment";
import { useSnackbar } from "notistack";
import useAuth from "../../hooks/useAuth";
import { APP_HOME_PATH } from "../../config/routes";
import MedicationIcon from "@mui/icons-material/Medication";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";

const TreatmentDetail = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const isSuperAdmin = user?.roles?.some(
    (r) => r === "super_admin" || r?.name === "super_admin"
  );
  const tenantQ = searchParams.get("tenant_id");
  const tenantParam =
    isSuperAdmin && tenantQ ? Number(tenantQ) : null;
  const profileQuery =
    tenantParam != null ? `?tenant_id=${tenantParam}` : "";
  const [treatment, setTreatment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    const fetchTreatment = async () => {
      try {
        const data = await getTreatmentById(id, tenantParam);
        setTreatment(data);
      } catch (err) {
        setError(err.message || "Failed to fetch treatment details");
        enqueueSnackbar("Failed to load treatment details", {
          variant: "error",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchTreatment();
  }, [id, tenantParam, enqueueSnackbar]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!treatment) {
    return (
      <Box sx={{ mt: 4 }}>
        <Alert severity="warning">Treatment not found</Alert>
      </Box>
    );
  }

  const InfoRow = ({ label, value, tint = "primary" }) => (
    <Stack
      direction="row"
      spacing={1.5}
      alignItems="flex-start"
      sx={{ py: 0.35, width: "100%", minWidth: 0 }}
    >
      <Typography
        variant="caption"
        sx={{
          color: "text.secondary",
          width: { xs: 118, sm: 150 },
          maxWidth: { xs: 118, sm: 150 },
          flexShrink: 0,
          fontWeight: 600,
          letterSpacing: 0.02,
          bgcolor: (t) =>
            t.palette.mode === "dark"
              ? "rgba(255,255,255,0.06)"
              : tint === "teal"
                ? "rgba(0, 137, 123, 0.12)"
                : "rgba(21, 101, 192, 0.1)",
          px: 1,
          py: 0.75,
          borderRadius: 1,
        }}
      >
        {label}
      </Typography>
      <Typography
        component="div"
        variant="body2"
        sx={{
          flex: 1,
          minWidth: 0,
          pt: 0.25,
          fontWeight: 500,
          wordBreak: "break-word",
        }}
      >
        {value ?? "—"}
      </Typography>
    </Stack>
  );

  const scopeLabel =
    treatment.treatment_scope === "group" ? "Group / Herd" : "Individual";

  const formatDosage = (dosage, unit) => {
    const raw = String(dosage ?? "").trim();
    if (!raw) return "—";
    if (/[a-zA-Z]/.test(raw)) return raw;
    const u = String(unit ?? "").trim();
    return u ? `${raw} ${u}` : raw;
  };

  const formatTemperature = (temperature) => {
    const raw = String(temperature ?? "").trim();
    if (!raw) return "—";
    if (raw.includes("°")) return raw;
    const n = Number(raw);
    if (Number.isFinite(n)) return `${raw} °C`;
    return raw;
  };

  return (
    <Box
      sx={{
        minHeight: "100%",
        pb: 4,
        pt: { xs: 2, md: 3 },
        px: { xs: 2, md: 3 },
        bgcolor: "background.default",
      }}
    >
      <Box sx={{ width: "100%", maxWidth: { xs: "100%", md: 1280 }, mx: "auto" }}>
      <Breadcrumbs sx={{ mb: 2, flexWrap: "wrap" }}>
        <MuiLink component={Link} underline="hover" color="inherit" to={APP_HOME_PATH}>
          Dashboard
        </MuiLink>
        <MuiLink
          component={Link}
          underline="hover"
          color="inherit"
          to={tenantParam != null ? `/treatments?tenant_id=${tenantParam}` : "/treatments"}
        >
          Treatments
        </MuiLink>
        <Typography color="text.primary">
          {treatment.treatment_id}
        </Typography>
      </Breadcrumbs>
      <Paper
        elevation={0}
        sx={{
          mb: 2.5,
          p: { xs: 2.5, md: 3.5 },
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
          boxShadow: 1,
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ xs: "stretch", sm: "center" }}
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={2} alignItems="center" sx={{ minWidth: 0 }}>
            <Box
              sx={{
                width: { xs: 48, sm: 52 },
                height: { xs: 48, sm: 52 },
                borderRadius: 2,
                display: "grid",
                placeItems: "center",
                bgcolor: "primary.main",
                color: "primary.contrastText",
                boxShadow: "0 6px 16px rgba(21,101,192,0.28)",
              }}
            >
              <MedicationIcon sx={{ fontSize: { xs: 26, sm: 30 } }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                component="h1"
                sx={{
                  fontWeight: 800,
                  letterSpacing: -0.4,
                  color: "text.primary",
                  lineHeight: 1.2,
                  fontSize: { xs: "1.3rem", sm: "2rem" },
                }}
              >
                Treatment #{treatment.treatment_id}
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 0.75, gap: 0.75 }}>
                <Chip
                  size="small"
                  label={scopeLabel}
                  sx={{ fontWeight: 700, bgcolor: "rgba(21,101,192,0.12)", color: "primary.dark" }}
                />
                <Chip
                  size="small"
                  label={treatment.medicine || "Medicine"}
                  sx={{ fontWeight: 700, bgcolor: "rgba(0,137,123,0.12)", color: "teal" }}
                />
              </Stack>
            </Box>
          </Stack>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ width: { xs: "100%", sm: "auto" } }}>
            <Button
              variant="contained"
              startIcon={<ArrowBackIcon />}
              component={Link}
              to={
                tenantParam != null
                  ? `/treatments?tenant_id=${tenantParam}`
                  : "/treatments"
              }
              sx={{ borderRadius: 2, width: { xs: "100%", sm: "auto" }, boxShadow: "0 4px 12px rgba(21,101,192,0.24)" }}
            >
              Back to list
            </Button>
            <Button
              variant="outlined"
              startIcon={<EditOutlinedIcon />}
              component={Link}
              to={`/treatment/${id}/edit${profileQuery}`}
              sx={{ borderRadius: 2, width: { xs: "100%", sm: "auto" }, borderColor: "teal", color: "teal" }}
            >
              Edit treatment
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Box
        sx={{
          display: "grid",
          gap: 2.5,
          gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
          alignItems: "stretch",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            borderRadius: 2,
            border: "1px solid",
            borderColor: "rgba(21, 101, 192, 0.24)",
            borderLeft: "5px solid",
            borderLeftColor: "primary.main",
            boxShadow: "0 6px 24px rgba(21, 101, 192, 0.08)",
          }}
        >
          <Typography variant="subtitle1" fontWeight={800} sx={{ color: "primary.dark", letterSpacing: 0.2 }}>
            Treatment information
          </Typography>
          <Divider sx={{ my: 2, opacity: 0.85 }} />
          <Stack spacing={1.25}>
            <InfoRow label="Treatment scope" value={scopeLabel} />
            <InfoRow
              label={treatment.treatment_scope === "group" ? "Group" : "Animal tag"}
              value={
                treatment.treatment_scope === "group"
                  ? treatment.group_name || "—"
                  : treatment.animal_tag_number || treatment.animal_id || "—"
              }
            />
            {treatment.treatment_scope === "group" && (
              <InfoRow label="Group size" value={treatment.group_size ?? "—"} />
            )}
            {treatment.treatment_scope === "group" && (
              <InfoRow label="Application method" value={treatment.application_method || "—"} />
            )}
            <InfoRow label="Treatment ID" value={treatment.treatment_id} />
            <InfoRow
              label="Date"
              value={treatment.date ? new Date(treatment.date).toLocaleDateString() : "—"}
            />
            <InfoRow label="Time" value={treatment.time || "—"} />
            <InfoRow label="Condition" value={treatment.treatment_of || "—"} />
            <InfoRow label="Symptoms" value={treatment.signs_symptoms || "—"} />
          </Stack>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            borderRadius: 2,
            border: "1px solid",
            borderColor: "rgba(0, 137, 123, 0.26)",
            borderLeft: "5px solid",
            borderLeftColor: "teal",
            boxShadow: "0 6px 24px rgba(0, 137, 123, 0.08)",
          }}
        >
          <Typography variant="subtitle1" fontWeight={800} sx={{ color: "teal", letterSpacing: 0.2 }}>
            Medication details
          </Typography>
          <Divider sx={{ my: 2, opacity: 0.85 }} />
          <Stack spacing={1.25}>
            <InfoRow tint="teal" label="Medicine" value={treatment.medicine || "—"} />
            <InfoRow tint="teal" label="Brand" value={treatment.brand || "—"} />
            <InfoRow
              tint="teal"
              label="Dosage"
              value={formatDosage(treatment.dosage, treatment.dispensed_unit)}
            />
            <InfoRow
              tint="teal"
              label="Next dosage"
              value={
                treatment.next_dosage
                  ? new Date(treatment.next_dosage).toLocaleDateString()
                  : "N/A"
              }
            />
            <InfoRow
              tint="teal"
              label="Temperature"
              value={formatTemperature(treatment.temperature)}
            />
            <InfoRow tint="teal" label="Prescribed by" value={treatment.prescribed_by || "—"} />
          </Stack>
        </Paper>
      </Box>

      <Paper
        elevation={0}
        sx={{
          mt: 2.5,
          p: 2.5,
          borderRadius: 2,
          border: "1px solid",
          borderColor: "rgba(2, 136, 209, 0.22)",
          borderLeft: "5px solid",
          borderLeftColor: "info.main",
          boxShadow: "0 6px 24px rgba(2, 136, 209, 0.08)",
        }}
      >
        <Typography variant="subtitle1" fontWeight={800} sx={{ color: "info.dark", letterSpacing: 0.2 }}>
          Clinical notes
        </Typography>
        <Divider sx={{ my: 2, opacity: 0.85 }} />
        <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", color: "text.primary" }}>
          {treatment.comment || "No comments recorded for this treatment."}
        </Typography>
      </Paper>
      </Box>
    </Box>
  );
};

export default TreatmentDetail;
